import {
  isWriteFenceStaleError,
  subscribeFenceClosed,
  subscribeFenceReopened,
} from './persistenceV2/writeFence';
import { logger } from './useSessionRecordingStore/lib/logger';

/** What a tracked unit of audio work is, so a drain can wait on a subset. */
export type AudioWorkKind = 'chunk-upload' | 'backup-upload' | 'other';

export interface AudioWorkContext {
  readonly generation: number;
  readonly signal: AbortSignal;
  isCurrent: () => boolean;
  throwIfStale: () => void;
}

const cancelledError = (): Error => {
  const error = new Error('audio work generation cancelled');
  error.name = 'AbortError';
  return error;
};

let acceptingAudioWork = true;
let audioWorkGeneration = 0;
let generationController = new AbortController();
const pendingAudioWork = new Map<Promise<unknown>, AudioWorkKind>();
const audioWorkShutdownSubscribers = new Set<() => void>();

const invalidateAudioWorkGeneration = (): void => {
  if (!acceptingAudioWork) return;
  acceptingAudioWork = false;
  generationController.abort(cancelledError());
  audioWorkGeneration += 1;
  generationController = new AbortController();
};

// Stop spawning work when logout closes the write fence in any tab.
subscribeFenceClosed((source) => {
  invalidateAudioWorkGeneration();
  if (source !== 'remote') return;
  audioWorkShutdownSubscribers.forEach((callback) => {
    try {
      callback();
    } catch {
      // A failing consumer must not prevent the other teardown callbacks.
    }
  });
});

// A reopened fence means writes are safe again (hydrate confirmed cleanup);
// without this, one transient fence close latches every runTrackedAudioWork
// call — hydration recovery included — until the next recording start.
subscribeFenceReopened(() => {
  acceptingAudioWork = true;
});

/** Notifies capture owners when another tab invalidates all audio writes. */
export const subscribeAudioWorkShutdown = (callback: () => void): (() => void) => {
  audioWorkShutdownSubscribers.add(callback);
  return () => audioWorkShutdownSubscribers.delete(callback);
};

/** Registers detached recorder/upload work so logout can drain it safely. */
export const runTrackedAudioWork = async <T>(
  work: (context: AudioWorkContext) => Promise<T>,
  kind: AudioWorkKind
): Promise<T | undefined> => {
  if (!acceptingAudioWork) {
    logger.warn({
      event_type: 'AUDIO_WORK_SKIPPED_SHUTDOWN',
      details: { generation: audioWorkGeneration },
    });
    return undefined;
  }
  const generation = audioWorkGeneration;
  const signal = generationController.signal;
  const isCurrent = () =>
    acceptingAudioWork && generation === audioWorkGeneration && !signal.aborted;
  const context: AudioWorkContext = {
    generation,
    signal,
    isCurrent,
    throwIfStale: () => {
      if (isCurrent()) return;
      throw signal.reason instanceof Error ? signal.reason : cancelledError();
    },
  };
  const promise = work(context);
  pendingAudioWork.set(promise, kind);
  try {
    return await promise;
  } finally {
    pendingAudioWork.delete(promise);
  }
};

/**
 * Keeps a detached promise (an upload the caller no longer awaits) in the
 * drain set, so logout still waits for it. Attaching handlers here also
 * prevents an unhandled rejection on a promise nobody else consumes.
 */
export const trackDetachedAudioWork = (
  work: Promise<unknown> | void,
  kind: AudioWorkKind
): void => {
  const promise = Promise.resolve(work);
  pendingAudioWork.set(promise, kind);
  const cleanup = () => pendingAudioWork.delete(promise);
  void promise.then(cleanup, cleanup);
};

export const beginAudioShutdown = () => {
  invalidateAudioWorkGeneration();
};

export const resumeAudioWork = () => {
  acceptingAudioWork = true;
};

export const isAudioWorkEnabled = (context?: AudioWorkContext) =>
  context ? context.isCurrent() : acceptingAudioWork;

export const getPendingAudioWorkCount = () => pendingAudioWork.size;

/**
 * Catch handler for recorder dispatch promises. Fence-stale and cancelled
 * rejections are already logged upstream and would only surface as an unhandled
 * error from a media event handler. Anything else keeps propagating.
 */
export const swallowAudioWorkCutoff = (err: unknown): void => {
  if (isWriteFenceStaleError(err)) return;
  if (err instanceof Error && err.name === 'AbortError') return;
  throw err;
};

export const waitForSettledAudioWork = async (
  work: Iterable<Promise<unknown>>,
  timeoutMs: number
): Promise<boolean> => {
  const drained = Promise.allSettled([...work]).then(() => true);
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timedOut = new Promise<false>((resolve) => {
    timer = setTimeout(() => resolve(false), timeoutMs);
  });
  try {
    return await Promise.race([drained, timedOut]);
  } finally {
    clearTimeout(timer);
  }
};

export const drainAudioWork = (
  timeoutMs: number,
  opts?: { exclude?: readonly AudioWorkKind[] }
): Promise<boolean> => {
  const exclude = opts?.exclude;
  const work = exclude
    ? [...pendingAudioWork]
        .filter(([, kind]) => !exclude.includes(kind))
        .map(([promise]) => promise)
    : [...pendingAudioWork.keys()];
  return waitForSettledAudioWork(work, timeoutMs);
};

/** Still-pending work by kind, for a drain timeout to say what it waited on. */
export const getPendingAudioWorkKinds = (
  exclude?: readonly AudioWorkKind[]
): Record<string, number> => {
  const counts: Record<string, number> = {};
  for (const kind of pendingAudioWork.values()) {
    if (exclude?.includes(kind)) continue;
    counts[kind] = (counts[kind] ?? 0) + 1;
  }
  return counts;
};

export const __resetAudioWorkBarrierForTests = () => {
  generationController.abort(cancelledError());
  audioWorkGeneration += 1;
  generationController = new AbortController();
  acceptingAudioWork = true;
  pendingAudioWork.clear();
};
